import { VerifiedTypeSchema } from '@sweetistics/contracts';
import { z } from 'zod';

import { deserializeActionData, type SerializedActionPayload } from '@/lib/actions/serialization';
import type { ProfileResponse } from '@/lib/validation/api';
import { ProfileResponseSchema } from '@/lib/validation/api';
import { UserEntitiesSchema } from '@/lib/validation/core/user-entities';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import { TweetAuthorAffiliationSchema } from '@/types/tweet/tweet';

export const PROFILE_CACHE_PREFIX = 'profile:';
export const USERNAME_CACHE_PREFIX = 'username:';
export const DEBUG_NAMESPACE = 'userPreviewCache';
export const COLLECTION_ID = 'user-preview-cache';
export const LEGACY_STORAGE_KEY = 'sweetistics:user-preview-cache';

export type UserPreviewCacheDetailLevel = 'partial' | 'full';

export interface UserPreviewCacheEntry {
  aliases: string[];
  detail: UserPreviewCacheDetailLevel;
  error: boolean;
  fetchedAt: number;
  preview?: UserPreviewVM;
  profile?: ProfileResponse;
  usernameKey?: string;
}

export type UserPreviewCachePatch = Partial<Omit<UserPreviewCacheEntry, 'fetchedAt' | 'preview' | 'profile'>> & {
  preview?: UserPreviewVM | SerializedActionPayload | null;
  profile?: ProfileResponse | SerializedActionPayload | null;
  fetchedAt?: number;
  detail?: UserPreviewCacheDetailLevel;
  profileId?: string | null;
  username?: string | null;
};

export const UserPreviewSnapshotSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  verifiedType: VerifiedTypeSchema,
  description: z.string().nullable().optional(),
  profileImageUrl: z.string().nullable().optional(),
  profileBannerUrl: z.string().nullable().optional(),
  totalInteractions: z.number().nonnegative().optional(),
  followersCount: z.number().nonnegative().optional(),
  followingCount: z.number().nonnegative().optional(),
  tweetCount: z.number().nonnegative().optional(),
  listedCount: z.number().nonnegative().optional(),
  affiliations: z.array(TweetAuthorAffiliationSchema).optional(),
  primaryAffiliation: TweetAuthorAffiliationSchema.nullable().optional(),
  location: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  entities: UserEntitiesSchema.optional(),
  createdAt: z.date().nullable().optional(),
  isFollowing: z.boolean().optional(),
  followsYou: z.boolean().optional(),
});

export const SerializedPayloadSchema = z.object({
  __devalue__payload: z.string(),
});

export const UserPreviewCacheRecordSchema = z.object({
  primaryCacheKey: z.string(),
  profileId: z.string().nullable().optional(),
  usernameKey: z.string().nullable().optional(),
  preview: z.union([UserPreviewSnapshotSchema, SerializedPayloadSchema]).nullable().optional(),
  profile: z.union([ProfileResponseSchema, SerializedPayloadSchema]).nullable().optional(),
  error: z.boolean(),
  fetchedAt: z.number().nonnegative(),
  detail: z.enum(['partial', 'full']),
  aliases: z.array(z.string()).min(1),
});

type InferUserPreviewCacheRecord = z.infer<typeof UserPreviewCacheRecordSchema>;

export type UserPreviewCacheRecord = Omit<
  InferUserPreviewCacheRecord,
  'preview' | 'profile' | 'profileId' | 'usernameKey'
> & {
  preview?: UserPreviewVM;
  profile?: ProfileResponse;
  profileId?: string;
  usernameKey?: string;
};

export type UserPreviewCacheStorageRecord = Omit<UserPreviewCacheRecord, 'preview' | 'profile'> & {
  preview?: SerializedActionPayload;
  profile?: SerializedActionPayload;
};

export interface UserPreviewCacheSnapshot {
  capturedAt: number;
  entries: UserPreviewCacheRecord[];
}

export interface UserPreviewCacheLookupMetadata {
  cacheKey?: string;
  primaryKey?: string;
  usernameKey?: string;
}

export const isSerializedPayload = (value: unknown): value is SerializedActionPayload =>
  SerializedPayloadSchema.safeParse(value).success;

export const decodePayload = <T>(value: T | SerializedActionPayload | null | undefined): T | undefined => {
  if (value === null || value === undefined) {
    return;
  }

  if (isSerializedPayload(value)) {
    return deserializeActionData<T>(value);
  }

  return value;
};

export const decodeUserPreviewPayload = (
  value: z.infer<typeof UserPreviewSnapshotSchema> | SerializedActionPayload | null | undefined
): UserPreviewVM | undefined => {
  if (!value) {
    return;
  }

  if (isSerializedPayload(value)) {
    return decodePayload<UserPreviewVM>(value);
  }

  const parsed = UserPreviewSnapshotSchema.safeParse(value);
  if (!parsed.success) {
    return;
  }

  const snapshot = parsed.data;
  const preview: UserPreviewVM = {
    id: snapshot.id,
    username: snapshot.username,
    name: snapshot.name,
    verifiedType: snapshot.verifiedType,
  };

  if (snapshot.description !== undefined) {
    preview.description = snapshot.description ?? null;
  }
  if (snapshot.profileImageUrl !== undefined) {
    preview.profileImageUrl = snapshot.profileImageUrl ?? null;
  }
  if (snapshot.profileBannerUrl !== undefined) {
    preview.profileBannerUrl = snapshot.profileBannerUrl ?? null;
  }
  if (snapshot.totalInteractions !== undefined) {
    preview.totalInteractions = snapshot.totalInteractions;
  }
  if (snapshot.followersCount !== undefined) {
    preview.followersCount = snapshot.followersCount;
  }
  if (snapshot.followingCount !== undefined) {
    preview.followingCount = snapshot.followingCount;
  }
  if (snapshot.tweetCount !== undefined) {
    preview.tweetCount = snapshot.tweetCount;
  }
  if (snapshot.listedCount !== undefined) {
    preview.listedCount = snapshot.listedCount;
  }
  if (snapshot.affiliations !== undefined) {
    preview.affiliations = snapshot.affiliations;
  }
  if (snapshot.primaryAffiliation !== undefined) {
    preview.primaryAffiliation = snapshot.primaryAffiliation;
  }
  if (snapshot.location !== undefined) {
    preview.location = snapshot.location ?? null;
  }
  if (snapshot.url !== undefined) {
    preview.url = snapshot.url ?? null;
  }
  if (snapshot.entities !== undefined) {
    preview.entities = snapshot.entities;
  }
  if (snapshot.createdAt !== undefined) {
    preview.createdAt = snapshot.createdAt ?? null;
  }
  if (snapshot.isFollowing !== undefined) {
    preview.isFollowing = snapshot.isFollowing;
  }
  if (snapshot.followsYou !== undefined) {
    preview.followsYou = snapshot.followsYou;
  }

  return preview;
};
